import config from '#config'

import { modulesInConfiguration } from '../../services/flowOrchestrationService/flowModules'
import {
	AuthenticateSmartcheckSessionDto,
	ModuleDefinition,
	ModuleDefinitionType,
} from '../../types/authenticateSmartcheckSessionDto'
import {
	FlowStepGroup,
	FlowStepSubGroup,
} from '#modules/LandingPage/landingPage.types'

export { FlowStepGroup, FlowStepSubGroup }

export const mapModuleToModuleRoute = (
	module: ModuleDefinitionType,
): ModuleRoute[] => {
	let route: string
	switch (module) {
		case ModuleDefinition.LandingPage:
			route = config.routeCodes.flowSplash
			break
		case ModuleDefinition.PersonalInformationQuestionnaire:
		case ModuleDefinition.PersonalInformationExtendedQuestionnaire:
		case ModuleDefinition.PersonalInformationCprOnlyQuestionnaire:
		case ModuleDefinition.HouseholdQuestionnaire:
		case ModuleDefinition.HouseholdSingleCohabitantQuestionnaire:
		case ModuleDefinition.AssetQuestionnaire:
		case ModuleDefinition.CarExtendedQuestionnaire:
		case ModuleDefinition.MbfQuestionnaire:
			route = config.routeCodes.consumerInputs
			break
		case ModuleDefinition.CustomQuestionnaire:
			route = config.routeCodes.customQuestionnaire
			break
		case ModuleDefinition.Eskat:
			route = config.routeCodes.eskat
			break
		case ModuleDefinition.Pension:
			route = config.routeCodes.pension
			break
		case ModuleDefinition.Bank:
			return [
				{ module, route: '/banks/reopen-session' },
				{
					module,
					route: '/banks/banks',
				},
				{
					module,
					route: '/banks/bank-login',
				},
				{
					module,
					route: '/banks/bank-session-overview',
				},
			]
		case ModuleDefinition.DebtOverview:
			route = config.routeCodes.debtOverview
			break
		case ModuleDefinition.AssetOverview:
			route = config.routeCodes.assetOverview
			break
		case ModuleDefinition.AssetExpenses:
			return [
				{
					module,
					route: `${config.routeCodes.assetExpenses}`,
				},
				{
					module,
					route: `${config.routeCodes.assetExpenses}/property-link`,
				},
				{
					module,
					route: `${config.routeCodes.assetExpenses}/property-budget`,
				},
				{
					module,
					route: `${config.routeCodes.assetExpenses}/vehicle-link`,
				},
				{
					module,
					route: `${config.routeCodes.assetExpenses}/vehicle-budget`,
				},
			]
		case ModuleDefinition.AssetReplacement:
			route = config.routeCodes.assetReplacement
			break
		case ModuleDefinition.SavingsCapacity:
			route = config.routeCodes.savingsCapacity
			break
		case ModuleDefinition.CustomQuestionnaire:
			route = config.routeCodes.customQuestionnaire
			break
		case ModuleDefinition.Budget:
			route = config.routeCodes.miniBudget
			break
		case ModuleDefinition.IncomeVerification:
			route = config.routeCodes.incomeVerification
			break
		case ModuleDefinition.BudgetConsumerEmail:
			return [
				{
					module,
					route: config.routeCodes.budgetConsumerEmail,
				},
				{
					module,
					route: config.routeCodes.budgetConsumerEmailSent,
				},
			]
		case ModuleDefinition.SavingsCapacity:
			return [
				{
					module,
					route: config.routeCodes.savingsCapacity,
				},
			]
		default:
			throw new Error(`ModuleDefinitionType: ${module} is unknown`)
	}

	return [{ module, route }]
}

export interface ModuleRoute {
	module: ModuleDefinitionType
	route: string
}

export interface FlowStepData {
	group: FlowStepGroup
	subGroup: FlowStepSubGroup
	modules: ModuleRoute[]
}

export const getBackgroundInformationStep = (
	moduleIds: ModuleDefinitionType[],
): FlowStepData | null => {
	const { modules } = moduleIds.reduce(
		(acc, moduleId) => {
			switch (moduleId) {
				case ModuleDefinition.PersonalInformationQuestionnaire:
				case ModuleDefinition.AssetQuestionnaire:
				case ModuleDefinition.CarExtendedQuestionnaire:
				case ModuleDefinition.HouseholdQuestionnaire:
				case ModuleDefinition.HouseholdSingleCohabitantQuestionnaire:
				case ModuleDefinition.PersonalInformationCprOnlyQuestionnaire:
				case ModuleDefinition.PersonalInformationExtendedQuestionnaire:
				case ModuleDefinition.CustomQuestionnaire:
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
			}
			return acc
		},
		{
			modules: [] as ModuleRoute[],
		},
	)
	if (modules.length <= 0) {
		return null
	}

	return {
		group: FlowStepGroup.BackgroundInformation,
		subGroup: FlowStepSubGroup.BackgroundInformation,
		modules,
	}
}

export const getDataStep = (
	moduleIds: ModuleDefinitionType[],
): FlowStepData | null => {
	const {
		modules,
		includesBank,
		includesEskat,
		includesPension,
		includesSavingsCapacity,
	} = moduleIds.reduce(
		(acc, moduleId) => {
			switch (moduleId) {
				case ModuleDefinition.Eskat:
					acc.includesEskat = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.Pension:
					acc.includesPension = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.Bank:
					acc.includesBank = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
			}
			return acc
		},
		{
			modules: [] as ModuleRoute[],
			includesEskat: false,
			includesPension: false,
			includesBank: false,
			includesSavingsCapacity: false,
		},
	)

	const flowStepData = {
		group: FlowStepGroup.Data,
		modules,
	}

	// includes only eskat
	if (
		includesEskat &&
		!includesBank &&
		!includesPension &&
		!includesSavingsCapacity
	) {
		return { ...flowStepData, subGroup: FlowStepSubGroup.TaxInformation }
	}

	// includes only bank
	if (
		includesBank &&
		!includesEskat &&
		!includesPension &&
		!includesSavingsCapacity
	) {
		return { ...flowStepData, subGroup: FlowStepSubGroup.BankConnect }
	}

	// includes only pension
	if (
		includesPension &&
		!includesEskat &&
		!includesBank &&
		!includesSavingsCapacity
	) {
		return { ...flowStepData, subGroup: FlowStepSubGroup.Pension }
	}

	// includes only savings capacity
	if (
		includesSavingsCapacity &&
		!includesEskat &&
		!includesBank &&
		!includesPension
	) {
		return { ...flowStepData, subGroup: FlowStepSubGroup.SavingsCapacity }
	}

	// includes only eskat and pension
	if (
		includesEskat &&
		includesPension &&
		!includesBank &&
		!includesSavingsCapacity
	) {
		return {
			...flowStepData,
			subGroup: FlowStepSubGroup.TaxInformationWithPension,
		}
	}

	// Does not include any of the above modules
	if (
		!includesEskat &&
		!includesBank &&
		!includesPension &&
		!includesSavingsCapacity
	) {
		return null
	}

	return { ...flowStepData, subGroup: FlowStepSubGroup.DataSharing }
}

export const getOverviewStep = async (
	caseSession: AuthenticateSmartcheckSessionDto,
): Promise<FlowStepData | null> => {
	const moduleConfiguration =
		caseSession.smartCheckConfiguration.moduleConfiguration
	const moduleIds = (await modulesInConfiguration(caseSession)).map(
		(module) => module.type,
	)
	const {
		modules,
		includesBudget,
		includesDebtOverview,
		includesAssetOverview,
		includesAssetExpenses,
		includesIncomeVerification,
		includesSavingsCapacity,
	} = moduleIds.reduce(
		(acc, moduleId) => {
			switch (moduleId) {
				case ModuleDefinition.Budget:
					acc.includesBudget = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.DebtOverview:
					acc.includesDebtOverview = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.AssetOverview:
					acc.includesAssetOverview = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.AssetExpenses:
					acc.includesAssetExpenses = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.AssetReplacement:
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.SavingsCapacity:
					acc.includesSavingsCapacity = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
				case ModuleDefinition.IncomeVerification:
					acc.includesIncomeVerification = true
					acc.modules = acc.modules.concat(mapModuleToModuleRoute(moduleId))
					break
			}
			return acc
		},
		{
			modules: [] as ModuleRoute[],
			includesBudget: false,
			includesSavingsCapacity: false,
			includesDebtOverview: false,
			includesAssetOverview: false,
			includesAssetExpenses: false,
			includesIncomeVerification: false,
		},
	)

	if (includesBudget && !includesDebtOverview && !includesIncomeVerification) {
		return {
			group: FlowStepGroup.Overview,
			subGroup:
				moduleConfiguration.budgetType === 'v2LastYearAverages'
					? FlowStepSubGroup.CreationOfHistoricalAverages
					: FlowStepSubGroup.CreationOfBudget,
			modules,
		}
	}

	if (includesDebtOverview && !includesBudget && !includesIncomeVerification) {
		return {
			group: FlowStepGroup.Overview,
			subGroup: FlowStepSubGroup.OverviewOfDebt,
			modules,
		}
	}
	if (
		(includesAssetOverview || includesAssetExpenses) &&
		!includesBudget &&
		!includesIncomeVerification
	) {
		return {
			group: FlowStepGroup.Overview,
			subGroup: FlowStepSubGroup.OverviewOfAssets,
			modules,
		}
	}
	if (includesIncomeVerification && !includesBudget && !includesDebtOverview) {
		return {
			group: FlowStepGroup.Overview,
			subGroup: FlowStepSubGroup.OverviewOfIncome,
			modules,
		}
	}
	if (includesDebtOverview && (includesBudget || includesIncomeVerification)) {
		return {
			group: FlowStepGroup.Overview,
			subGroup: FlowStepSubGroup.OverviewOfYourFinances,
			modules,
		}
	}
	if (
		includesSavingsCapacity &&
		!includesBudget &&
		!includesAssetExpenses &&
		!includesDebtOverview &&
		!includesIncomeVerification
	) {
		return {
			modules,
			group: FlowStepGroup.Overview,
			subGroup: FlowStepSubGroup.SavingsCapacity,
		}
	}

	return null
}

export const getFlowSteps = async (
	caseSession: AuthenticateSmartcheckSessionDto,
): Promise<FlowStepData[]> => {
	const flowSteps: FlowStepData[] = []
	const moduleIds = (await modulesInConfiguration(caseSession)).map(
		(module) => module.type,
	)
	const backgroundInformationStep = getBackgroundInformationStep(moduleIds)
	const dataStep = getDataStep(moduleIds)
	const overviewStep = await getOverviewStep(caseSession)

	if (backgroundInformationStep !== null) {
		flowSteps.push(backgroundInformationStep)
	}
	if (dataStep !== null) {
		flowSteps.push(dataStep)
	}
	if (overviewStep !== null) {
		flowSteps.push(overviewStep)
	}

	return flowSteps
}
